import { LegacyCard } from "../card/LegacyCard";
import { H1, H6, SmallButton } from "@sellar-io/components";
import { OrderGetResponseType } from "@sellar-io/api-v2/types";
import {
  calculateItemTotal,
  calculateVatTotal,
  formatCurrency,
} from "@sellar-io/common";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";
import { useSellarUser } from "../../hooks";
import { useOrder } from "../../hooks/api/v2/order/useOrder";
import { useParamAsNumber } from "../../hooks/useParamAsNumber";
import { ORDER_INVOICE } from "../../constants/routes";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import {
  renderDeliveryDateTitle,
  renderTaxName,
} from "../../utils/sellarHelpers";
import { SafariPrintAlert } from "../alert/SafariPrintAlert";
import { Badge } from "../badge/Badge";
import { NavigationBar } from "../navigation/NavigationBar";
import { ProfilePicture } from "../profilePicture/ProfilePicture";
import { SectionHeader } from "../sectionHeader/SectionHeader";
import { SpinnerLegacy } from "..";
import { generateOrderNumber, getOrderStage } from "./helpers";
import { OrderAddress } from "./OrderAddress";
import { OrderSummaryContainer } from "./OrderSummaryContainer";

export const OrderInvoice = (): React.ReactElement => {
  const orderId = useParamAsNumber(ORDER_INVOICE, "orderId");

  const { business } = useSellarCurrentBusiness();
  const navigate = useNavigate();

  const query = useOrder(orderId);

  useEffect(() => {
    if (query.data) {
      document.title = `${generateOrderNumber(query.data)}${
        query.data.Supplier.Business
          ? business?.Retailer
            ? ` - ${query.data.Supplier.Business.name}`
            : ` - ${query.data.Retailer.Business.name}`
          : ""
      }`;
    }
  }, [query.data, business?.Retailer]);

  if (query.isLoading) return <SpinnerLegacy />;

  return (
    <>
      <NavigationBar
        title={"Order Invoice"}
        leftIcon={"CHEVRON_LEFT"}
        onClickLeftIcon={() => navigate(-1)}
      />
      {query.data && <OrderInvoiceContent order={query.data} />}
    </>
  );
};

const OrderInvoiceContent = ({ order }: { order: OrderGetResponseType }) => {
  const { business } = useSellarCurrentBusiness();
  const { user } = useSellarUser();

  const orderStage = () => getOrderStage(order);

  return (
    <div
      className="mx-auto flex max-w-[900px] flex-col gap-2 space-y-2 p-2 print:grayscale"
      style={{ zoom: "80%" }}
    >
      <SafariPrintAlert />
      <SmallButton className={"print:hidden"} onClick={() => window.print()}>
        Print
      </SmallButton>
      <div>
        <div className="flex justify-between">
          <img
            src={order.Supplier.Business.logoUrl ?? ""}
            className="my-auto block h-20 object-contain"
            alt={order.Supplier.Business.name}
          />
          <div className="flex flex-col select-text">
            <span>{order.Supplier.Business.InvoiceAddress.name}</span>
            <span>{order.Supplier.Business.InvoiceAddress.street}</span>
            <span>{order.Supplier.Business.InvoiceAddress.city}</span>
            <span>{order.Supplier.Business.InvoiceAddress.region}</span>
            <span>{order.Supplier.Business.InvoiceAddress.postalCode}</span>
            <span>{order.Supplier.Business.InvoiceAddress.country}</span>
          </div>
        </div>
        <div>
          <div className="flex justify-between pt-4">
            {business?.Supplier && order.deliveryNumber && (
              <span className={"flex"}>
                Delivery number: {order.deliveryNumber}
              </span>
            )}
            <span>
              Invoice #
              {order.customInvoiceNumber ? order.customInvoiceNumber : order.id}
            </span>
            <span>{order.Supplier.Business.email}</span>
            {business?.Supplier && order.Supplier.Business.phoneNumber && (
              <span>
                {user?.name} - {order.Supplier.Business.phoneNumber}{" "}
              </span>
            )}
          </div>
          <div className="flex justify-between">
            {order.Supplier.SupplierPaymentInformation?.bankSortCode && (
              <span>
                Sort code:{" "}
                <strong>
                  {order.Supplier.SupplierPaymentInformation.bankSortCode}
                </strong>
              </span>
            )}
            <br />
            {order.Supplier.SupplierPaymentInformation?.bankAccountNumber && (
              <span>
                Account number:{" "}
                <strong>
                  {order.Supplier.SupplierPaymentInformation.bankAccountNumber}
                </strong>
              </span>
            )}
          </div>
          <div className="flex justify-between">
            {order.Supplier.Business.awrsNumber && (
              <span>
                AWRS: <strong>{order.Supplier.Business.awrsNumber}</strong>
              </span>
            )}
            {order.Supplier.Business.vatNumber && (
              <span>VAT No: {order.Supplier.Business.vatNumber}</span>
            )}
            {order.Supplier.Business.companyNumber && (
              <span>Company No: {order.Supplier.Business.companyNumber}</span>
            )}
          </div>
        </div>
      </div>

      <LegacyCard>
        <SectionHeader
          icon={
            business?.Supplier ? (
              <ProfilePicture
                logoUrl={order.Retailer.Business.logoUrl}
                name={order.Retailer.Business.name}
              />
            ) : (
              <ProfilePicture
                logoUrl={order.Supplier.Business.logoUrl}
                name={order.Supplier.Business.name}
              />
            )
          }
          title={
            <H1 className={twMerge(business?.Supplier && "cursor-pointer")}>
              {business?.Supplier
                ? order.Retailer.Business.name
                : order.Supplier.Business.name}
            </H1>
          }
          subtitle={<H6>{generateOrderNumber(order)}</H6>}
        />
        <div className="flex flex-wrap gap-2">
          <Badge className="text-secondary">
            Ordered on {dayjs(order.createdAt).format("DD/MM/YY")}
          </Badge>
          {order.confirmedAt && order.paymentDue && (
            <Badge className="text-secondary">
              Payment due on {dayjs(order.paymentDue).format("DD/MM/YY")}
            </Badge>
          )}
          {order.deliveryPlannedAt && (
            <Badge className="text-secondary">
              {renderDeliveryDateTitle(order.fulfillmentType)} on{" "}
              {dayjs(order.deliveryPlannedAt).format("DD/MM/YY")}
            </Badge>
          )}
        </div>
      </LegacyCard>

      <OrderSummaryContainer
        items={order.OrderItems.map((orderItem) => ({
          description: orderItem.Product.name ?? "",
          quantity: orderItem.quantity,
          cost: formatCurrency(orderItem.price),
          sku: orderItem.Product.sku,
          promotionAppliedAt: orderItem.promotionAppliedAt,
        }))}
        priceProps={{
          delivery:
            orderStage() !== "Requested"
              ? formatCurrency(order.deliveryCharge, order.currency)
              : undefined,
          subTotal: formatCurrency(
            calculateItemTotal(order.OrderItems),
            order.currency,
          ),
          discount:
            order.discountAmount > 0
              ? {
                  value: formatCurrency(order.discountAmount, order.currency),
                  calculatedTotal: formatCurrency(
                    order.discountAmount,
                    order.currency,
                  ),
                }
              : undefined,
          tax: {
            name: renderTaxName(order.currency),
            percentage: order.vatPercentage.toString(),
            total: formatCurrency(
              calculateVatTotal({ ...order, orderItems: order.OrderItems }),
              order.currency,
            ),
          },
          total: formatCurrency(order.price, order.currency),
        }}
      />

      <LegacyCard className="flex justify-between gap-2">
        <div>
          <OrderAddress
            address={order.DeliveryAddress}
            title="Delivery Address"
          />
        </div>
        <div>
          <OrderAddress
            address={order.InvoiceAddress}
            title="Invoice Address"
          />
        </div>
      </LegacyCard>
    </div>
  );
};
